import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from "@tanstack/react-router";
import type { WorkQueueScope } from "@tex/finance-shared";
import { isLocalPath } from "./lib/api";
import { STAGE_LABELS } from "./lib/format";
import { AcceptInvitationPage } from "./routes/accept-invitation";
import { WIZARD_STEPS, type WizardStep } from "./lib/application-form";
import { ApplicationPage } from "./routes/application";
import { CaseDetailPage } from "./routes/case-detail";
import { CaseNewPage } from "./routes/case-new";
import { CasesListPage, type CasesSearch } from "./routes/cases-list";
import { DealerDetailPage } from "./routes/dealer-detail";
import { DealersListPage } from "./routes/dealers-list";
import { HomePage } from "./routes/home";
import { InvitePage } from "./routes/invite";
import { JurisdictionsPage } from "./routes/jurisdictions";
import { MyCasesPage } from "./routes/my-cases";
import { OutboxPage } from "./routes/outbox";
import { PartnerDetailPage } from "./routes/partner-detail";
import { PartnersListPage } from "./routes/partners-list";
import { ProgramDetailPage } from "./routes/program-detail";
import { AppShell } from "./routes/shell";
import { SignInPage, type SignInSearch } from "./routes/sign-in";
import { TEX_CASE_TABS, TexCasePage, type TexCaseTab } from "./routes/tex-case";
import { TexQueuePage } from "./routes/tex-queue";
import { TexReportsPage } from "./routes/tex-reports";

const QUEUE_SCOPES: readonly WorkQueueScope[] = ["mine", "unassigned", "all"];

const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomePage,
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  validateSearch: (search: Record<string, unknown>): SignInSearch => ({
    ...(typeof search.error === "string" ? { error: search.error } : {}),
    ...(search.step_up === 1 || search.step_up === "1" || search.step_up === true
      ? { step_up: 1 as const }
      : {}),
    ...(isLocalPath(search.redirect) ? { redirect: search.redirect } : {}),
  }),
  component: function SignInRoute() {
    return <SignInPage search={signInRoute.useSearch()} />;
  },
});

const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accept-invitation/$invitationId",
  component: function AcceptInvitationRoute() {
    const { invitationId } = acceptInvitationRoute.useParams();
    return <AcceptInvitationPage invitationId={invitationId} />;
  },
});

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite/$token",
  component: function InviteRoute() {
    const { token } = inviteRoute.useParams();
    return <InvitePage token={token} />;
  },
});

const myCasesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/my-cases",
  component: MyCasesPage,
});

const outboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/outbox",
  component: OutboxPage,
});

const dealersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dealers",
  component: DealersListPage,
});

const dealerDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dealers/$dealerId",
  component: function DealerDetailRoute() {
    const { dealerId } = dealerDetailRoute.useParams();
    return <DealerDetailPage dealerId={dealerId} />;
  },
});

const str = (v: unknown): string | undefined =>
  typeof v === "string" && v ? v : undefined;

const casesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cases",
  validateSearch: (search: Record<string, unknown>): CasesSearch => {
    const stage = str(search.stage);
    return {
      ...(stage && stage in STAGE_LABELS
        ? { stage: stage as CasesSearch["stage"] }
        : {}),
      ...(str(search.q) ? { q: str(search.q) } : {}),
      ...(str(search.dealer) ? { dealer: str(search.dealer) } : {}),
    };
  },
  component: function CasesRoute() {
    return <CasesListPage search={casesRoute.useSearch()} />;
  },
});

const caseNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cases/new",
  validateSearch: (search: Record<string, unknown>): { dealer?: string } =>
    str(search.dealer) ? { dealer: str(search.dealer) } : {},
  component: function CaseNewRoute() {
    return <CaseNewPage search={caseNewRoute.useSearch()} />;
  },
});

const caseDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cases/$caseId",
  component: function CaseDetailRoute() {
    const { caseId } = caseDetailRoute.useParams();
    return <CaseDetailPage caseId={caseId} />;
  },
});

const applicationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cases/$caseId/apply",
  validateSearch: (search: Record<string, unknown>): { step?: WizardStep } => {
    const step = str(search.step);
    return step && (WIZARD_STEPS as readonly string[]).includes(step)
      ? { step: step as WizardStep }
      : {};
  },
  component: function ApplicationRoute() {
    const { caseId } = applicationRoute.useParams();
    const { step } = applicationRoute.useSearch();
    return <ApplicationPage caseId={caseId} step={step} />;
  },
});

const partnersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tex/partners",
  component: PartnersListPage,
});

const partnerDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tex/partners/$partnerId",
  component: function PartnerDetailRoute() {
    const { partnerId } = partnerDetailRoute.useParams();
    return <PartnerDetailPage partnerId={partnerId} />;
  },
});

const programDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tex/programs/$programId",
  component: function ProgramDetailRoute() {
    const { programId } = programDetailRoute.useParams();
    return <ProgramDetailPage programId={programId} />;
  },
});

const jurisdictionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tex/jurisdictions",
  component: JurisdictionsPage,
});

const texReportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tex/reports",
  component: TexReportsPage,
});

const texQueueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tex/queue",
  validateSearch: (
    search: Record<string, unknown>,
  ): { scope?: WorkQueueScope } => {
    const scope = str(search.scope);
    return scope && (QUEUE_SCOPES as readonly string[]).includes(scope)
      ? { scope: scope as WorkQueueScope }
      : {};
  },
  component: function TexQueueRoute() {
    const { scope } = texQueueRoute.useSearch();
    const navigate = texQueueRoute.useNavigate();
    return (
      <TexQueuePage
        scope={scope ?? "mine"}
        onScope={(s) => navigate({ search: { scope: s } })}
      />
    );
  },
});

const texCaseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tex/cases/$caseId",
  validateSearch: (search: Record<string, unknown>): { tab?: TexCaseTab } => {
    const tab = str(search.tab);
    return tab && (TEX_CASE_TABS as readonly string[]).includes(tab)
      ? { tab: tab as TexCaseTab }
      : {};
  },
  component: function TexCaseRoute() {
    const { caseId } = texCaseRoute.useParams();
    const { tab } = texCaseRoute.useSearch();
    const navigate = texCaseRoute.useNavigate();
    return (
      <TexCasePage
        caseId={caseId}
        tab={tab ?? "review"}
        onTab={(t) => navigate({ search: { tab: t } })}
      />
    );
  },
});

export const router = createRouter({
  routeTree: rootRoute.addChildren([
    homeRoute,
    signInRoute,
    acceptInvitationRoute,
    inviteRoute,
    myCasesRoute,
    outboxRoute,
    dealersRoute,
    dealerDetailRoute,
    casesRoute,
    caseNewRoute,
    caseDetailRoute,
    applicationRoute,
    partnersRoute,
    partnerDetailRoute,
    programDetailRoute,
    jurisdictionsRoute,
    texReportsRoute,
    texQueueRoute,
    texCaseRoute,
  ]),
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
